import { createClient, type SupabaseClient } from '@supabase/supabase-js'
import type { SyncSettings } from '@/types/settings'

/**
 * Cliente Supabase singleton, construido a partir de la configuración
 * de sync del usuario.
 *
 * No se instancia en módulo (porque la URL/key se conocen solo tras
 * configurar Ajustes). Se obtiene vía `getSupabase()`, que reconstruye
 * el cliente si cambian las credenciales.
 */

let cached: { client: SupabaseClient; url: string; key: string } | null = null

/** Nombre del bucket de Storage donde se guardan los EPUBs. */
export const EPUB_BUCKET = 'epubs'

/** Nombre del bucket de Storage donde se guardan las portadas. */
export const COVERS_BUCKET = 'covers'

/**
 * Devuelve el cliente Supabase para la configuración dada, creándolo
 * o reutilizando el cacheado si URL+key coinciden.
 * Devuelve null si la configuración está incompleta.
 */
export function getSupabase(sync: Pick<SyncSettings, 'supabaseUrl' | 'supabaseAnonKey'>): SupabaseClient | null {
  const { supabaseUrl, supabaseAnonKey } = sync
  if (!supabaseUrl || !supabaseAnonKey) return null

  if (cached && cached.url === supabaseUrl && cached.key === supabaseAnonKey) {
    return cached.client
  }

  const client = createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      storageKey: 'epub-unlimited-auth',
    },
  })

  cached = { client, url: supabaseUrl, key: supabaseAnonKey }
  return client
}

/** Invalida el cache (útil al cerrar sesión o cambiar credenciales). */
export function resetSupabaseClient() {
  cached = null
}
